import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "./contexts/AuthContext";
import api from "./api/axios";
import { getImageUrl } from "./utils/image";
import { useActivityLogger } from "./hooks/useActivity";
import { getRandomThumbnail } from "./utils/randomImage";
import { registerPushSubscription } from "./push/registerPush";

export default function HomeLoggedIn() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [showEmergencyModal, setShowEmergencyModal] = useState(false);
  const [videos, setVideos] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loadingVideos, setLoadingVideos] = useState(false);
  const [loadingArticles, setLoadingArticles] = useState(false);
  const { logUserActivity, loading: userLoading } = useActivityLogger();
  const [userData, setUserData] = useState(null);

  console.log("API Base:", process.env.REACT_APP_API_BASE);
  console.log("Image URL:", user?.image_url);

  useEffect(() => {
    // Fetch videos from API
    const fetchVideos = async () => {
      try {
        setLoadingVideos(true);
        const response = await api.get("/youtube/videos");
        let videoData = response.data || [];

        // LOGIKA SORTING BARU (SAMA SEPERTI CARI VIDEO)
        videoData.sort((a, b) => {
          // Cek validitas publishedAt
          const dateA = a.publishedAt ? new Date(a.publishedAt) : null;
          const dateB = b.publishedAt ? new Date(b.publishedAt) : null;

          const isValidA = dateA && !isNaN(dateA.getTime());
          const isValidB = dateB && !isNaN(dateB.getTime());

          // 1. Jika A punya tanggal valid, B tidak -> A di atas (-1)
          if (isValidA && !isValidB) return -1;

          // 2. Jika A tidak punya, B punya -> B di atas (1)
          if (!isValidA && isValidB) return 1;

          // 3. Jika keduanya punya tanggal valid -> Sort berdasarkan publishedAt (Terbaru di atas)
          if (isValidA && isValidB) {
            return dateB - dateA;
          }

          // 4. Jika keduanya TIDAK punya tanggal -> Sort berdasarkan CreatedAt (Terbaru di atas)
          const createdA = new Date(a.CreatedAt || a.created_at || 0);
          const createdB = new Date(b.CreatedAt || b.created_at || 0);
          return createdB - createdA;
        });

        // Get only the first 2 videos (latest)
        const latestVideos = videoData.slice(0, 2).map((video) => ({
          id: video.videoID || video.ID,
          title: video.title,
          thumbnail: video.thumbnail || "/video1.png",
          channelTitle: video.channelTitle || video.sumber,
          publishedAt: video.publishedAt,
        }));

        setVideos(latestVideos);
      } catch (error) {
        console.error("Failed to fetch videos:", error);
        // Fallback to static content if API fails
        setVideos([
          { id: 1, thumbnail: "/video1.png", title: "Video Edukasi 1" },
          { id: 2, thumbnail: "/video2.png", title: "Video Edukasi 2" },
        ]);
      } finally {
        setLoadingVideos(false);
      }
    };

    // Fetch articles from API
    const fetchArticles = async () => {
      try {
        setLoadingArticles(true);
        const response = await api.get("/articles/all");
        let articleData = response.data || [];

        // LOGIKA SORTING BARU (SAMA, TAPI MENGGUNAKAN published_date)
        articleData.sort((a, b) => {
          // Cek validitas published_date
          const dateA = a.published_date ? new Date(a.published_date) : null;
          const dateB = b.published_date ? new Date(b.published_date) : null;

          const isValidA = dateA && !isNaN(dateA.getTime());
          const isValidB = dateB && !isNaN(dateB.getTime());

          // 1. Jika A punya tanggal valid, B tidak -> A di atas (-1)
          if (isValidA && !isValidB) return -1;

          // 2. Jika A tidak punya, B punya -> B di atas (1)
          if (!isValidA && isValidB) return 1;

          // 3. Jika keduanya punya tanggal valid -> Sort berdasarkan published_date (Terbaru di atas)
          if (isValidA && isValidB) {
            return dateB - dateA;
          }

          // 4. Jika keduanya TIDAK punya tanggal -> Sort berdasarkan CreatedAt (Terbaru di atas)
          const createdA = new Date(a.CreatedAt || a.created_at || 0);
          const createdB = new Date(b.CreatedAt || b.created_at || 0);
          return createdB - createdA;
        });

        // Get only the first 2 articles (latest)
        const latestArticles = articleData.slice(0, 2).map((article) => ({
          id: article.ID || article.id,
          title: article.title,
          thumbnail:
            getImageUrl(article.image) ||
            getRandomThumbnail(article.title || article.id),
          author: article.author,
          publishedDate: article.published_date,
          url: article.url,
        }));

        setArticles(latestArticles);
      } catch (error) {
        console.error("Failed to fetch articles:", error);

        // Fallback to static content if API fails
        setArticles([
          { id: 1, thumbnail: "/artikel1.png", title: "Artikel Edukasi 1" },
          { id: 2, thumbnail: "/artikel2.png", title: "Artikel Edukasi 2" },
        ]);
      } finally {
        setLoadingArticles(false);
      }
    };

    const fetchUser = async () => {
      try {
        console.log("Fetching data for user ID:", user.id || user.ID);
        console.log("user: ", user);
        const response = await api.get("/users/" + (user.id || user.ID));
        console.log("Fetched user data:", response.data);
        setUserData(response.data);
      } catch (error) {
        console.error("Failed to fetch user data:", error);
      }
    };

    const setupPushNotifications = async () => {
      try {
        if (localStorage.getItem("push_registered") === "1") {
          console.log("Push already registered, skip");
          return;
        }
    
        const userId = user.id || user.ID; 
        await registerPushSubscription(userId);
    
        localStorage.setItem("push_registered", "1");
      } catch (error) {
        console.error("Push notification setup failed:", error);
      }
    };

    fetchVideos();
    fetchArticles();
    fetchUser();
    if (!userLoading) {
      logUserActivity(
        "user",
        "view_homepage",
        "HomeLoggedIn",
        "User viewed homepage after login"
      );
      const token = localStorage.getItem("access_token");
      if (token) {
        setupPushNotifications();
      }
    }
  }, [userLoading, user]);

  const handleVideoClick = (videoId) => {
    logUserActivity(
      "user",
      "click_homepage_video",
      "HomeLoggedIn",
      `User clicked video from homepage: ${videoId}`
    );
    navigate(`/video/${videoId}`);
  };

  const handleArticleClick = (article) => {
    logUserActivity(
      "user",
      "click_homepage_article",
      "HomeLoggedIn",
      `User clicked article from homepage: ${article.title || article.id}`
    );

    if (article.id) {
      navigate(`/article/${article.id}`);
    } else {
      console.warn("No ID found for article:", article);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        <p className="ml-2">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white px-4 py-6">
      {/* Top section */}
      <div className="flex items-center justify-between mb-6 mt-4">
        <div className="flex items-center gap-2">
          <img src="/logohallostroke.png" alt="Logo" className="h-10" />
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowEmergencyModal(true)}
            className="flex items-center gap-1 text-red-600 border border-red-500 px-3 py-1 rounded-full text-sm hover:bg-red-50 transition"
          >
            <img
              src="/emergency-phone.png"
              alt="Emergency"
              className="w-4 h-4 mr-1"
            />
            <span>Darurat</span>
          </button>
          <button onClick={() => window.open("docs/user-manual.pdf", "_blank")}>
            <img
              src="/button-user-guide.png"
              alt="User Guide"
              className="w-7 h-7 ml-1"
            />
          </button>
          <Link to="/notif">
            <img src="/icon-bell.png" className="w-7 h-7" alt="Notifikasi" />
          </Link>
        </div>
      </div>

      {/* Greeting */}
      <div className="relative w-full rounded-xl overflow-hidden mb-6 h-24">
        <img
          src="/updatedbg.png"
          alt="Header"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="relative flex items-start justify-between gap-3 px-4 py-6 h-full">
          <div className="flex items-start gap-3">
            <button onClick={() => navigate("/profile")}>
              <img
                src={
                  user?.image_url ? getImageUrl(user.image_url) : "/avatar.png"
                }
                alt="Avatar"
                className="w-10 h-10 rounded-full object-cover bg-white/80 mt-1"
              />
            </button>
            <div>
              <p className="text-base font-semibold text-blue-500">
                Hallo, {user?.username || "User"}!
              </p>
              <p className="text-sm text-gray-700">
                Lagi mau cari info apa, nih?
              </p>
            </div>
          </div>

          <div className="flex items-center">
            <p className="text-sm font-xs text-blue-700 bg-white/80 px-2 py-1 rounded-3xl">
              {userData && userData.length > 0 ? userData[0].name : ""}
            </p>
          </div>
        </div>
      </div>

      {/* Menu Cards */}
      <div className="grid grid-cols-5 gap-3 mb-10">
        <Link to="/forum">
          <button className="flex flex-col items-center text-center hover:bg-gray-100 rounded-lg py-2 transition">
            <img
              src="/icon-forum.png"
              className="w-16 h-16 mb-1"
              alt="Forum Diskusi"
            />
            <p className="text-xs font-medium text-gray-700">Forum Diskusi</p>
          </button>
        </Link>

        <Link to="/reminders">
          <button className="flex flex-col items-center text-center hover:bg-gray-100 rounded-lg py-2 transition">
            <img
              src="/icon-pengingat.png"
              className="w-16 h-16 mb-1"
              alt="Pengingat"
            />
            <p className="text-xs font-medium text-gray-700">Pengingat</p>
          </button>
        </Link>

        <Link to="/telekonsultasi">
          <button className="flex flex-col items-center text-center hover:bg-gray-100 rounded-lg py-2 transition">
            <img
              src="/icon-tkonsul.png"
              className="w-16 h-16 mb-1"
              alt="Tele-konsultasi"
            />
            <p className="text-xs font-medium text-gray-700">Tele-konsultasi</p>
          </button>
        </Link>

        <Link to="/online-community">
          <button className="flex flex-col items-center text-center hover:bg-gray-100 rounded-lg py-2 transition">
            <img
              src="/icon-komunitas.png"
              className="w-16 h-16 mb-1"
              alt="Komunitas Online"
            />
            <p className="text-xs font-medium text-gray-700">
              Komunitas Online
            </p>
          </button>
        </Link>

        <Link to="/faskes">
          <button className="flex flex-col items-center text-center hover:bg-gray-100 rounded-lg py-2 transition">
            <img
              src="/icon-faskes.png"
              className="w-16 h-16 mb-1"
              alt="Faskes Stroke"
            />
            <p className="text-xs font-medium text-gray-700">Faskes Stroke</p>
          </button>
        </Link>
      </div>

      {/* Video Section */}
      <div className="mb-10">
        <div className="flex justify-between items-center mb-4">
          <h2 className="font-semibold">Video Edukasi</h2>
          <Link to="/videos">
            <button className="flex items-center gap-1 px-3 py-1 border border-blue-500 text-blue-500 text-sm rounded-full hover:bg-blue-50 transition">
              <img src="/search-blue.png" alt="Search" className="w-4 h-4" />
              <span>Cari Video</span>
            </button>
          </Link>
        </div>

        {loadingVideos ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
            <p className="ml-2 text-sm text-gray-600">Memuat video...</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4">
            {videos.map((vid) => (
              <div
                key={vid.id}
                className="cursor-pointer hover:opacity-80 transition"
                onClick={() => handleVideoClick(vid.id)}
              >
                <img
                  src={vid.thumbnail}
                  className="rounded-lg shadow-md w-full h-auto"
                  alt={vid.title || `Video ${vid.id}`}
                  onError={(e) => {
                    e.target.src = vid.id === 1 ? "/video1.png" : "/video2.png";
                  }}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="flex justify-between items-center mb-4">
          <h2 className="font-semibold">Artikel Edukasi</h2>
          <Link to="/articles">
            <button className="flex items-center gap-1 px-3 py-1 border border-blue-500 text-blue-500 text-sm rounded-full hover:bg-blue-50 transition">
              <img src="/search-blue.png" alt="Search" className="w-4 h-4" />
              <span>Cari Artikel</span>
            </button>
          </Link>
        </div>

        {loadingArticles ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
            <p className="ml-2 text-sm text-gray-600">Memuat artikel...</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4">
            {articles.map((article) => (
              <div
                key={article.id}
                className="cursor-pointer hover:opacity-80 transition"
                onClick={() => handleArticleClick(article)}
              >
                <div
                  className="flex items-center justify-center bg-white rounded-lg shadow-md w-full overflow-hidden"
                  style={{ aspectRatio: "16/9" }}
                >
                  <img
                    src={article.thumbnail}
                    className="object-cover w-full h-full"
                    alt={article.title || `Artikel ${article.id}`}
                    style={{ background: "#fff" }}
                    onError={(e) => {
                      e.target.src =
                        article.id === 1 ? "/artikel1.png" : "/artikel2.png";
                    }}
                  />
                </div>
                {article.title && (
                  <div className="mt-2">
                    <h4 className="text-sm font-medium text-gray-800 line-clamp-2">
                      {article.title}
                    </h4>
                    {article.author && (
                      <p className="text-xs text-gray-500 mt-1">
                        {article.author}
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {showEmergencyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-lg p-6 max-w-sm w-full text-center mx-4">
            <h3 className="text-lg font-semibold mb-2">Panggil Bantuan?</h3>
            <p className="text-sm text-gray-700 mb-6">
              Kami akan menghubungkan Anda dengan call center layanan darurat
            </p>
            <div className="flex justify-around text-sm font-medium">
              <button
                onClick={() => setShowEmergencyModal(false)}
                className="text-gray-500 hover:underline"
              >
                TIDAK
              </button>
              <button
                onClick={() => {
                  setShowEmergencyModal(false);
                  window.location.href = "tel:112";
                }}
                className="text-blue-600 hover:underline"
              >
                YA
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}